<template>
  <div class="insurance-professionals">
    <div class="stenci-group stenci-alert text-center">
      <h6 class="stenci-title text-secondary">
        Gestão de atendimento por plano de saúde
      </h6>

      <div class="form-group">
        Defina se este plano de saúde terá restrição de atendimento por profissional.
      </div>

      <ul>
        <li>
          <strong>Lista vazia:</strong> todos os profissionais da clínica poderão atender
          pacientes vinculados a este plano, respeitando apenas as regras das agendas.
        </li>
        <li>
          <strong>Com profissionais adicionados:</strong> o atendimento ficará restrito
          exclusivamente aos profissionais selecionados nesta lista.
        </li>
      </ul>

      <div class="mb-2" v-if="!isNew">
        <button class="btn btn-secondary btn-icon btn-icon-left"
                :disabled="saving || deleting"
                @click="addProfessional">
          <fa-icon :icon="['fal', 'plus']" />Adicionar profissionais
        </button>
      </div>
      <div class="text-bold text-error" v-else>Para adicionar, primeiro salve o cadastro!</div>
    </div>

    <div class="loading loading-lg" v-if="loading || saving" />

    <template v-else>
      <div class="empty mt-2" v-if="data.items.length === 0">
        <div class="empty-icon">
          <fa-icon :icon="['fal', 'info-circle']" size="2x"/>
        </div>
        <p class="empty-title h5">Lista de profissionais</p>
        <p class="empty-subtitle">
          Nenhum profissional adicionado, clique no botão abaixo para adicionar.
        </p>
      </div>
      <table class="table table-hover" v-else>
        <thead>
        <tr>
          <th>#</th>
          <th>Nome do profissional</th>
          <th style="width: 200px">Código na operadora</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(item, i) in data.items" :key="i">
          <td>{{ i + 1 }}</td>
          <td>{{ item.name }}</td>
          <td>
            <input
              type="text"
              class="form-input input-sm"
              v-model="item.contractCode"
              :disabled="saving || deleting"
              @change="updateProfessional(item)"
              v-mask="'00000000000000000000'"
              placeholder="000000"
            />
          </td>
          <td class="text-right">
            <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                    :class="{loading: deleting && idx === i}"
                    :disabled="saving || deleting || updatingIds.includes(item.id)"
                    data-tooltip="Excluir"
                    @click="removeProfessional(item)">
              <fa-icon :icon="['fal', 'times']" />
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </template>

    <find-professional-modal
      v-if="findProfessionalModal.show"
      @selected="selectedProfessionals"
      @close="findProfessionalModal.show = false"
    />
  </div>
</template>

<script>
import FindProfessionalModal from './FindProfessionalModal.vue';

export default {
  props: {
    isNew: {
      type: Boolean,
      default: true,
    },
    insuranceId: {
      type: String,
    },
    planId: {
      type: String,
    },
  },
  components: {
    FindProfessionalModal,
  },
  data() {
    return {
      idx: null,
      loading: false,
      saving: false,
      deleting: false,
      updatingIds: [],
      pendingIds: [],
      findProfessionalModal: {
        show: false,
      },
      data: {
        items: [],
        hasMore: false,
        limit: 0,
        offset: 0,
      },
    };
  },
  async mounted() {
    if (!this.isNew) {
      this.init();
    }
  },
  methods: {
    init() {
      this.loadInsuranceProfessionals();
    },
    loadInsuranceProfessionals() {
      this.loading = true;
      const { insuranceId, planId } = this;

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      return this.$http
        .get(`/insurances/${insuranceId}/plans/${planId}/professionals`, { params })
        .then(({ data }) => {
          this.data.items = data.items.map(item => ({
            id: item.professional.id,
            name: item.professional.name,
            contractCode: item.contractCode,
          }));
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        }).finally(() => {
          this.loading = false;
        });
    },
    addProfessional() {
      this.findProfessionalModal.show = true;
    },
    selectedProfessionals(professionals) {
      if (this.saving || this.deleting) {
        return null;
      }

      this.saving = true;
      this.findProfessionalModal.show = false;

      const savedIds = this.data.items.map(item => item.id);
      professionals = professionals.filter(item => !savedIds.includes(item.id));

      professionals.forEach((professional) => {
        professional.contractCode = null;
      });

      if (professionals.length === 0) {
        this.saving = false;
        return null;
      }

      const { insuranceId, planId } = this;

      return this.$http
        .post(`/insurances/${insuranceId}/plans/${planId}/professionals`, { professionals })
        .then(() => {
          this.data.items = [...this.data.items, ...professionals];
          this.$toast.success('Profissionais adicionados com sucesso!');
        })
        .catch(() => {
          this.$toast.error('Erro ao carregar os dados!');
        }).finally(() => {
          this.saving = false;
        });
    },
    updateProfessional(professional) {
      if (this.updatingIds.includes(professional.id)) {
        if (!this.pendingIds.includes(professional.id)) {
          this.pendingIds.push(professional.id);
        }
        return null;
      }

      this.updatingIds.push(professional.id);

      const { insuranceId, planId } = this;

      const data = {
        professionals: [professional],
      };

      return this.$http
        .post(`/insurances/${insuranceId}/plans/${planId}/professionals`, data)
        .then(() => {
          this.$toast.success('Registro atualizado!');
        })
        .catch(() => {
          this.$toast.error('Erro ao salvar o código na operadora!');
        }).finally(() => {
          this.updatingIds = this.updatingIds.filter(id => id !== professional.id);
          this.findProfessionalModal.show = false;

          if (this.pendingIds.includes(professional.id)) {
            this.pendingIds = this.pendingIds.filter(id => id !== professional.id);
            this.updateProfessional(professional);
          }
        });
    },
    removeProfessional(item) {
      if (this.deleting) {
        return;
      }

      this.idx = this.data.items.indexOf(item);
      this.deleting = true;
      this.pendingIds = this.pendingIds.filter(id => id !== item.id);

      const { insuranceId, planId } = this;

      this.$http
        .delete(`/insurances/${insuranceId}/plans/${planId}/professionals/${item.id}`)
        .then(() => {
          this.data.items.splice(this.idx, 1);
        })
        .catch(() => {
          this.$toast.error('Erro ao excluir o profissional!');
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
</style>
